/**
 * Progress Linear
 * @module components/ProgressLinear
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
// Import variables
import './_variables.scss';
// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { styleMap } from 'lit/directives/style-map.js';
// Import types
import { ProgressLinearType, ProgressLinearStatus, DEFAULT_PROGRESS_LINEAR_THRESHOLDS, } from './types';
// Import style for web component
import styles from './web-component-style.scss?inline';
/**
 * @element uas-progress-linear
 * @summary A linear progress indicator with three rendering modes.
 *
 * @description
 * The `type` attribute is the canonical mode switch:
 *
 * 1. `determinate` (default) - Bar width follows `value` (0-100). The host reflects a `status` attribute (`success | warning | error`) derived from `value` and the active `thresholds`, which the SCSS uses to recolor the fill.
 * 2. `indeterminate` - Continuous animation for unknown-duration operations.
 * 3. `unlimited` - 100%-width drifting dotted texture signalling an unmetered resource. Status recoloring is suppressed. Respects `prefers-reduced-motion`.
 *
 * The component features a smooth entry animation where the height grows from 0 to full height. All visual properties can be customized via CSS custom properties.
 *
 * @attr {'determinate' | 'indeterminate' | 'unlimited'} [type=determinate] - The rendering mode. Maps to the `type` property.
 * @attr {number} [value=0] - Progress percentage (0-100). Only applies in determinate mode. Maps to the `value` property.
 *
 * **Customizable variables:**
 * @cssprop --uas--progress-linear--custom-height - Override the height of the progress bar.
 * @cssprop --uas--progress-linear--custom-background - Override the background color of the track.
 * @cssprop --uas--progress-linear--custom-value-background - Override the color of the progress indicator. Wins over status fills and the unlimited base color.
 * @cssprop --uas--progress-linear--custom-wrapper-border-radius - Override the border radius of the progress bar wrapper.
 * @cssprop --uas--progress-linear--custom-bar-border-radius - Override the border radius of the progress bar.
 * @cssprop --uas--progress-linear--custom-animation-duration - Override the indeterminate animation duration.
 * @cssprop --uas--progress-linear--custom-transition-height - Override the entry transition timing.
 *
 * **Default variables (read-only, use for reference):**
 * @cssprop --uas--progress-linear--height - Default height (4px).
 * @cssprop --uas--progress-linear--background - Default track background color.
 * @cssprop --uas--progress-linear--value-background - Default progress indicator color.
 * @cssprop --uas--progress-linear--value-background-success - Fill color when status is `success`.
 * @cssprop --uas--progress-linear--value-background-warning - Fill color when status is `warning`.
 * @cssprop --uas--progress-linear--value-background-error - Fill color when status is `error`.
 * @cssprop --uas--progress-linear--unlimited-dot-color - Dot color for the unlimited texture.
 * @cssprop --uas--progress-linear--unlimited-dot-radius - Radius of each dot.
 * @cssprop --uas--progress-linear--unlimited-dot-spacing - Horizontal distance between adjacent dots.
 * @cssprop --uas--progress-linear--unlimited-drift-distance - Drift distance per animation cycle.
 * @cssprop --uas--progress-linear--unlimited-drift-duration - Drift cycle duration.
 * @cssprop --uas--progress-linear--wrapper-border-radius - Default border radius (12px).
 * @cssprop --uas--progress-linear--bar-border-radius - Default border radius (12px).
 * @cssprop --uas--progress-linear--animation-duration - Default animation duration (1.5s).
 * @cssprop --uas--progress-linear--transition-height - Default height transition (500ms ease-in-out).
 *
 * @example Determinate progress at 50%
 * ```html
 * <uas-progress-linear value="50"></uas-progress-linear>
 * ```
 *
 * @example Indeterminate progress
 * ```html
 * <uas-progress-linear type="indeterminate"></uas-progress-linear>
 * ```
 *
 * @example Unlimited
 * ```html
 * <uas-progress-linear type="unlimited"></uas-progress-linear>
 * ```
 *
 * @example Custom thresholds
 * ```html
 * <uas-progress-linear value="80" .thresholds=${ { warning: 50, error: 75 } }></uas-progress-linear>
 * ```
 *
 * @since 7.0.0-rc.2 Initial implementation.
 * @since 8.3.0 Replaced the `indeterminate` boolean attribute with a `type` enum (`determinate | indeterminate | unlimited`). Added `thresholds` property and computed `status` getter for threshold-based recoloring.
 */
export class ProgressLinearComponent extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Rendering mode of the progress bar.
         *
         * @attr type
         * @since 8.3.0 Initial implementation.
         */
        this.type = ProgressLinearType.DETERMINATE;
        /**
         * The current progress value as a percentage (0-100).
         *
         * @attr value
         */
        this.value = 0;
        /**
         * Thresholds for the success / warning / error transitions on the determinate fill.
         *
         * @since 8.3.0 Initial implementation.
         */
        this.thresholds = DEFAULT_PROGRESS_LINEAR_THRESHOLDS;
    }
    /**
     * Status classification of the current `value` against `thresholds` while `type` is `determinate`. Returns `undefined` outside determinate mode and for non-finite `value`.
     *
     * @since 8.3.0 Initial implementation.
     *
     * @return The classified status, or `undefined` when not applicable.
     */
    get status() {
        if (this.type !== ProgressLinearType.DETERMINATE) {
            return undefined;
        }
        if (!Number.isFinite(this.value)) {
            return undefined;
        }
        const t = this.thresholds ?? DEFAULT_PROGRESS_LINEAR_THRESHOLDS;
        const clamped = Math.max(0, Math.min(100, this.value));
        if (clamped >= t.error) {
            return ProgressLinearStatus.ERROR;
        }
        if (clamped >= t.warning) {
            return ProgressLinearStatus.WARNING;
        }
        return ProgressLinearStatus.SUCCESS;
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('value')) {
            this.value = Math.max(0, Math.min(100, this.value));
        }
    }
    render() {
        const progressStyles = {
            '--progress-value': `${this.value / 100}`,
        };
        return html `
			<div
				class="progress-bar"
				type=${this.type}
				status=${ifDefined(this.status)}
			>
				<div class="progress-bar-value" style=${styleMap(progressStyles)}></div>
			</div>
		`;
    }
}
/**
 * Defines the component's CSS styles.
 */
ProgressLinearComponent.styles = css `${unsafeCSS(styles)}`;
__decorate([
    property({ type: String, reflect: true })
], ProgressLinearComponent.prototype, "type", void 0);
__decorate([
    property({ type: Number })
], ProgressLinearComponent.prototype, "value", void 0);
__decorate([
    property({ type: Object, attribute: false })
], ProgressLinearComponent.prototype, "thresholds", void 0);
// Auto-register the component with safety check to prevent duplicate definitions
if (!customElements.get('uo-legacy-progress-linear')) {
    customElements.define('uo-legacy-progress-linear', ProgressLinearComponent);
}
